import React, { useEffect, useState, useRef, Fragment } from 'react';
import cn from 'classnames';
import * as PropTypes from 'prop-types';
import axios from 'axios';
import Fuse from 'fuse.js';

import endpoints from '../../define/endpoints';
import { Dropdown } from '../common/Dropdown';
import { DROPDOWN_MBL_BEHAVIOUR, DROPDOWN_POSITIONS } from '../../../constants';
import { checkIsMobileBrowser } from '../../utils/checkIsMobileBrowser';
import debounce from 'lodash.debounce';

import { Icon } from '@iconify/react';
import iconLoading from '@iconify-icons/mdi/loading';
import iconMapMarker from '@iconify-icons/mdi/map-marker';
import iconSadness from '@iconify-icons/mdi/emoticon-sad-outline';
import roundGpsFixed from '@iconify/icons-ic/round-gps-fixed';
import bulbOnSolid from '@iconify/icons-teenyicons/bulb-on-solid';

const SearchTermsAutocomplete = (props) => {
  const ref = useRef(null);
  const _NEXT_TO_ME = app_main_wpml.close_to_me;
  const _SUGGESTIONS = app_main_wpml.suggestions;
  const _ROME = app_main_wpml.rome;
  const _LAKE_GARDA = app_main_wpml.lake_garda;
  const _VALTELLINA = app_main_wpml.valtellina;
  const _LOMBARDY = app_main_wpml.lombardy;

  const [query, setQuery] = useState(props.selectedSearchTerm ?? '');
  const [searchTerms, setSearchTerms] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [cursor, setCursor] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSelected, setIsSelected] = useState(false);
  const defaultSuggestions = [_ROME, _LOMBARDY, _VALTELLINA, _LAKE_GARDA];

  const navigator_ab_test = document.querySelector('#navigator_usage')?.value;
  const setUserLocation = props.setUserLocation;
  const [isUserLocationEnabled, setIsUserLocationEnabled] = useState(false);
  const SCROLL_OFFSET = 70;

  navigator?.permissions?.query({ name: 'geolocation' }).then((res) => setIsUserLocationEnabled(["granted", "prompt"].includes(res.state)))

  useEffect(() => {
    ref.current.value = props.selectedSearchTerm ?? '';
  }, [props.selectedSearchTerm]);

  useEffect(() => {
    const loadSearchTerms = async () => {
      const response = await axios.get(`${endpoints.core}/search-terms`, {
        params: { locale: window.freedomeConfig.locale }
      });

      setSearchTerms(new Fuse(response.data, { keys: ['title'], threshold: 0.35, includeScore: true }));
      setIsLoading(false);
    };
    loadSearchTerms();
  }, []);

  useEffect(() => {
    if (isLoading === false) {
      updateSuggestions(ref.current.value, true);
    }
  }, [isLoading]);

  const handleFocus = () => {
    if (props.scrollOnFocus && checkIsMobileBrowser()) {
      window.setTimeout(function () {
        window.scrollTo(0, ref.current.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET);
      }, 50);
    }
    
    updateSuggestions(query, true);
    setIsDropdownOpen(true);
    setIsSelected(false);

    window.setTimeout(function () {
      ref.current.select();
    }, 1);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target) && isDropdownOpen) {
        if (!isSelected && suggestions.length > 0 && props.selectFirstSuggestionOnBlur) {
          selectSearchTerm(suggestions[0].item);
        } else {
          clearSelectedSearchTerm();
        }
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mouseup', handleClickOutside);
    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
    };
  });

  const updateSuggestions = debounce((query = '', init = false) => {
    let matches = [];

    if (isLoading === true) {
      return;
    }

    if (query.length > 2) {
      matches = searchTerms.search(query).sort(function (a, b) {
        return b.score < 0.0001 ? 1 : b.item.search_score + (100 - b.score * 100) - (a.item.search_score + (100 - a.score * 100));
      });
    }

    setSuggestions(matches);

    if (!init) {
      setIsSelected(false);
      setCursor(0);
      setQuery(query);
    }
  }, 50);

  const handleKeyboard = (e) => {
    switch (e.key) {
      case 'Tab':
      case 'Enter':
        if (suggestions.length > 0) {
          selectSearchTerm(suggestions[cursor].item);
          ref.current.blur();
        }
        break;

      case 'ArrowDown':
        if (suggestions.length > cursor && cursor < 4) {
          setCursor(cursor + 1);
        }
        break;

      case 'ArrowUp':
        if (cursor > 0) {
          setCursor(cursor - 1);
        }
        break;

      default:
        if (props.scrollOnFocus && checkIsMobileBrowser()) {
          window.setTimeout(function () {
            window.scrollTo(0, ref.current.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET);
          }, 50);
        }
    }
  };

  const registerUserPosition = (pos) => {
    localStorage.setItem('userLocation', JSON.stringify({
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude
    }));
    setUserLocation(JSON.parse(localStorage.getItem('userLocation')));
    selectSearchTermSimplified(_NEXT_TO_ME);
    setIsUserLocationEnabled(true);
  };

  const handleGeolocation = (action) => {
    navigator?.permissions?.query({ name: 'geolocation' }).then((result) => {
      if (result.state === 'granted') {
        selectSearchTermSimplified(_NEXT_TO_ME);
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            registerUserPosition(pos);
          })
      } else if (result.state === 'prompt') {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            selectSearchTermSimplified(_NEXT_TO_ME);
            registerUserPosition(pos);
            var analytics_data = {
              'event': 'navigator',
              'allowed': true
            };
            dataLayer.push(analytics_data)
          },
          (err) => {
            setIsUserLocationEnabled(false);
            var analytics_data = {
              'event': 'navigator',
              'allowed': false
            };
            dataLayer.push(analytics_data)
          });
      }
    });
  };

  const selectSearchTerm = (searchTerm) => {
    setQuery(searchTerm.title);
    props.onSearchTermSelect(searchTerm.title);
    setIsDropdownOpen(false);
    setIsSelected(true);
    ref.current.value = searchTerm.title;
  };

  const selectSearchTermSimplified = (searchTermTitle) => {
    selectSearchTerm({ title: searchTermTitle });
  };

  const clearSelectedSearchTerm = () => {
    props.onSearchTermSelect('');
    setQuery('');
    setIsSelected(false);
  };

  return (
    <div className="search-terms-autocomplete">
      <input
        ref={ref}
        type="text"
        className="input input--shadowed"
        placeholder={app_main_wpml.search_where_placeholder}
        onChange={(e) => updateSuggestions(e.target.value)}
        onFocus={() => handleFocus()}
        onKeyDown={(e) => handleKeyboard(e)}
        defaultValue={query}
      />

      <Dropdown
        position={DROPDOWN_POSITIONS.LEFT}
        mobileBehaviour={DROPDOWN_MBL_BEHAVIOUR.NONE}
        mobileLabel={app_main_wpml.search_where_placeholder}
        isOpen={isDropdownOpen}
      >
        <div className={'dropdown__content'}>
          {isLoading && (
            <div className="search-terms-autocomplete__empty-state">
              <Icon
                icon={iconLoading}
                className="icon icon--spin margin-right--1"
                inline
              />
              {app_main_wpml.loading}...
            </div>
          )}

          {!isLoading &&
            suggestions.length > 0 &&
            suggestions.slice(0, 5).map((searchTerm, index) => (
              <div
                key={searchTerm.item.id}
                className={cn('search-terms-autocomplete__suggestion', {
                  'search-terms-autocomplete__suggestion--highlighted':
                    cursor === index,
                })}
                onClick={() => selectSearchTerm(searchTerm.item)}
              >
                <Icon icon={iconMapMarker} className="icon icon" />
                {searchTerm.item.title}
              </div>
            ))}

          {!isLoading && suggestions.length === 0 && (
            <Fragment>
              {query == _NEXT_TO_ME || query.length < 3 ? (
                <Fragment>
                  {isUserLocationEnabled && (
                    <div
                      className='search-terms-autocomplete__suggestion'
                      onClick={() => {
                        handleGeolocation();
                      }}>
                      <Icon icon={roundGpsFixed} className="icon icon-big" />
                      {_NEXT_TO_ME}
                    </div>
                  )}
                  <div className="separator">
                    {_SUGGESTIONS}
                    <Icon
                      icon={bulbOnSolid}
                      className="icon color--warning margin-right--1"
                      inline
                    />
                  </div>
                  {defaultSuggestions.map(locationName => (
                    <div key={locationName}
                      className='search-terms-autocomplete__suggestion'
                      onClick={() => selectSearchTermSimplified(locationName)}>
                      <Icon icon={iconMapMarker} className="icon icon-secondary" />
                      {locationName}
                    </div>
                  ))}
                </Fragment>
              ) : (
                <div className="search-terms-autocomplete__empty-state">
                  <Icon icon={iconSadness} className="icon margin-right--1" inline />
                  {app_main_wpml.search_error}
                </div>
              )}
            </Fragment>
          )}
        </div>
      </Dropdown>
    </div>
  );
};

SearchTermsAutocomplete.defaultProps = {
  scrollOnFocus: false,
  selectFirstSuggestionOnBlur: true,
};

SearchTermsAutocomplete.propTypes = {
  scrollOnFocus: PropTypes.bool,
  selectFirstSuggestionOnBlur: PropTypes.bool,
  selectedSearchTerm: PropTypes.string,
  onSearchTermSelect: PropTypes.func.isRequired,
};

export default SearchTermsAutocomplete;
